import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Fade,
} from "@mui/material";
import { SketchPicker } from "react-color";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { createSetting, getAllSettings } from "../../service/settingService";
import Color from "../../components/Color";
import { applyTheme } from "../../utils/applyTheme";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "../../App"; // Import useSnackbar hook

const defaultColors = ["#21A1D3", "#D0021B", "#7C0BCC", "#CECCCCEE", "#9E9E9E", "#757575"];

const ColorPicker = ({ value, onChange, openPicker, onOpen, pickerKey }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box sx={{ position: "relative" }}>
      <Grid container spacing={isMobile ? 0.5 : 1} alignItems="center">
        {defaultColors.map((color, idx) => (
          <Grid item key={idx} xs={2} sm="auto">
            <Tooltip title={color} arrow>
              <Box
                sx={{
                  width: isMobile ? 28 : 30,
                  height: isMobile ? 28 : 30,
                  borderRadius: "10px",
                  bgcolor: color,
                  cursor: "pointer",
                  border: value === color ? "3px solid #d7d7d7" : "2px solid #f0f0f0",
                  transition: "all 0.25s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={() => onChange(color)}
              >
                {value === color && (
                  <CheckCircleIcon sx={{ color: "#fff", fontSize: isMobile ? 16 : 18 }} />
                )}
              </Box>
            </Tooltip>
          </Grid>
        ))}
        <Grid item xs={2} sm="auto">
          <Tooltip title="Pick Custom Color" arrow>
            <Box
              sx={{
                width: isMobile ? 32 : 34,
                height: isMobile ? 32 : 34,
                borderRadius: "10px",
                background: value || "#fafafa",
                cursor: "pointer",
                border: "2px solid transparent",
                backgroundImage: `linear-gradient(white, white), linear-gradient(135deg, #21A1D3, #7C0BCC)`,
                backgroundOrigin: "border-box",
                backgroundClip: "content-box, border-box",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: isMobile ? 14 : 16,
              }}
              onClick={() => onOpen(pickerKey)}
            >
              🎨
            </Box>
          </Tooltip>
        </Grid>
      </Grid>
      <Fade in={openPicker === pickerKey}>
        <Box
          sx={{
            mt: isMobile ? 0.5 : 1,
            position: "absolute",
            zIndex: 1300, // Higher z-index to ensure above other elements
            borderRadius: 3,
            boxShadow: "0px 8px 24px rgba(0,0,0,0.25)",
            overflow: "hidden",
            border: "1px solid #eee",
            width: isMobile ? "180px" : "220px",
            left: isMobile ? "-50px" : 0, // Adjust position for mobile
          }}
        >
          <SketchPicker
            width={isMobile ? "180px" : "220px"}
            color={value || "#ffffff"}
            onChangeComplete={(color) => onChange(color.hex)}
          />
        </Box>
      </Fade>
    </Box>
  );
};

const Settings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down("xs"));
  const { showSnackbar } = useSnackbar(); // Use the snackbar hook

  const [formData, setFormData] = useState({
    group: "invoice",
    company_name: "",
    gst_number: "",
    company_phone_number: "",
    billing_address: "",
    company_logo: null,
    website_primary_color: "",
    website_secondary_color: "",
    invoice_primary_color: "",
    invoice_secondary_color: "",
    invoice_footer: "",
    invoice_template: "",
  });

  const [logoPreview, setLogoPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [openPicker, setOpenPicker] = useState(null);

  useEffect(() => {
    const saved = localStorage.getItem("settings");
    if (saved) {
      const parsed = JSON.parse(saved);
      setFormData(parsed);
      applyTheme(parsed);
      setLogoPreview(parsed.company_logo || "");
    } else {
      fetchSettings();
    }
  }, []);

  const handleOpenPicker = (key) => {
    setOpenPicker((prev) => (prev === key ? null : key));
  };

  const fetchSettings = async () => {
    try {
      const res = await getAllSettings("invoice");
      if (res?.data?.data?.invoice) {
        const settingsObj = res.data.data.invoice;

        let logoURL = "";
        if (settingsObj.company_logo) {
          logoURL = settingsObj.company_logo.startsWith("http")
            ? settingsObj.company_logo
            : `${process.env.REACT_APP_API_BASE_URL || ""}${settingsObj.company_logo}`;
        }

        const updatedSettings = { ...settingsObj, company_logo: logoURL, group: "invoice" };
        localStorage.setItem("settings", JSON.stringify(updatedSettings));
        applyTheme(updatedSettings);
        setFormData(updatedSettings);
        setLogoPreview(logoURL);
      }
    } catch {
      showSnackbar("Failed to load settings", "error");
    }
  };

  const handleChange = (field, value) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const updated = { ...formData, company_logo: file };
      setFormData(updated);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setErrors({});
    try {
      const data = new FormData();
      data.append("group", formData.group);

      if (formData.company_logo instanceof File) {
        data.append("logo", formData.company_logo);
      }

      Object.entries(formData).forEach(([key, value]) => {
        if (key !== "group" && key !== "company_logo") {
          data.append(`settings[${key}]`, value ?? "");
        }
      });

      const res = await createSetting(data);
      const updated = res?.data?.data;
      if (!updated) throw new Error("No settings returned from server");

      showSnackbar("Settings updated successfully", "success");

      let logoURL = null;
      if (updated.company_logo) {
        logoURL = updated.company_logo.startsWith("http")
          ? updated.company_logo
          : `${process.env.REACT_APP_API_BASE_URL || ""}${updated.company_logo}`;
      }

      const finalSettings = { ...formData, ...updated, company_logo: logoURL };
      localStorage.setItem("settings", JSON.stringify(finalSettings));
      setLogoPreview(logoURL);
      setFormData(finalSettings);
      applyTheme(finalSettings);
    } catch (err) {
      if (err.response?.data?.errors) {
        const errorData = err.response.data.errors;
        setErrors(errorData);
        Object.entries(errorData).forEach(([field, messages]) => {
          messages.forEach((msg) => showSnackbar(msg, "error"));
        });
      } else {
        showSnackbar("Failed to update settings", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={isMobile ? 1 : 0} mt={isMobile ? 2 : 1.5}>
      <Paper
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 4,
          minHeight: { xs: "auto", md: 780 },
          width: "100%",
        }}
      >
        <Typography
          variant="h5"
          fontWeight={600}
          mb={isMobile ? 1 : 1.5}
          fontSize={{ xs: 18, sm: 20, md: 24 }}
        >
          Settings
        </Typography>

        {/* Logo */}
        <Typography
          variant="h6"
          fontSize={{ xs: 14, sm: 16 }}
          mb={isMobile ? 2 : 1.5}
        >
          Company Logo
        </Typography>
        <Grid container spacing={{ xs: 1, sm: 2 }}>
          <Grid item xs={12} sm={6}>
            <Button
              variant="outlined"
              component="label"
              sx={{
                width: "100%",
                height: { xs: 100, sm: 120 },
                border: "2px dashed #ccc",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                fontSize: { xs: 12, sm: 14 },
              }}
            >
              Upload Logo
              <input type="file" hidden onChange={handleFileChange} />
            </Button>
          </Grid>

          {logoPreview && (
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  width: "100%",
                  height: { xs: 100, sm: 140 },
                  position: "relative",
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid #eee",
                }}
              >
                <img
                  id="app-logo"
                  src={logoPreview}
                  alt="Logo"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 5,
                    right: 5,
                    bgcolor: "white",
                    color: "green",
                    fontSize: { xs: 16, sm: 18 },
                  }}
                >
                  <CheckCircleIcon fontSize="inherit" />
                </IconButton>
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Company Details */}
        <Typography
          variant="h6"
          fontSize={{ xs: 14, sm: 16 }}
          mt={isMobile ? 1.5 : 2}
          mb={isMobile ? 2 : 2}
        >
          Company Details
        </Typography>
        <Grid container spacing={{ xs: 2, sm: 2 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Company Name"
              fullWidth
              value={formData.company_name}
              onChange={(e) => handleChange("company_name", e.target.value)}
              sx={{
                "& .MuiInputBase-root": { fontSize: { xs: 14, sm: 16 } },
                "& .MuiInputLabel-root": { fontSize: { xs: 12, sm: 14 } },
                width: { xs: "100%" },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="GST Number"
              fullWidth
              value={formData.gst_number}
              onChange={(e) => handleChange("gst_number", e.target.value)}
              sx={{
                "& .MuiInputBase-root": { fontSize: { xs: 14, sm: 16 } },
                "& .MuiInputLabel-root": { fontSize: { xs: 12, sm: 14 } },
                width: { xs: "100%" },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Mobile Number"
              fullWidth
              value={formData.company_phone_number}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "");
                if (val.length <= 10) handleChange("company_phone_number", val);
              }}
              inputProps={{ maxLength: 10 }}
              sx={{
                "& .MuiInputBase-root": { fontSize: { xs: 14, sm: 16 } },
                "& .MuiInputLabel-root": { fontSize: { xs: 12, sm: 14 } },
                width: { xs: "100%" },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Billing Address"
              fullWidth
              value={formData.billing_address}
              onChange={(e) => handleChange("billing_address", e.target.value)}
              sx={{
                "& .MuiInputBase-root": { fontSize: { xs: 14, sm: 16 } },
                "& .MuiInputLabel-root": { fontSize: { xs: 12, sm: 14 } },
                width: { xs: "100%" },
              }}
            />
          </Grid>
        </Grid>

        {/* Dashboard Theme */}
        <Typography
          variant="h6"
          fontSize={{ xs: 14, sm: 16 }}
          mt={isMobile ? 1.5 : 2}
          mb={1}
        >
          Dashboard Theme
        </Typography>
        <Grid container spacing={{ xs: 1, sm: 2 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" mb={1} fontSize={{ xs: 12, sm: 14 }}>
              Primary Color
            </Typography>
            <ColorPicker
              value={formData.website_primary_color}
              onChange={(color) => handleChange("website_primary_color", color)}
              openPicker={openPicker}
              onOpen={handleOpenPicker}
              pickerKey="website_primary"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" mb={1} fontSize={{ xs: 12, sm: 14 }}>
              Secondary Color
            </Typography>
            <ColorPicker
              value={formData.website_secondary_color}
              onChange={(color) => handleChange("website_secondary_color", color)}
              openPicker={openPicker}
              onOpen={handleOpenPicker}
              pickerKey="website_secondary"
            />
          </Grid>
        </Grid>

        {/* Invoice Theme */}
        <Typography
          variant="h6"
          fontSize={{ xs: 14, sm: 16 }}
          mt={isMobile ? 1.5 : 2}
          mb={1}
        >
          Invoice Theme
        </Typography>
        <Grid container spacing={{ xs: 1, sm: 2 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" mb={1} fontSize={{ xs: 12, sm: 14 }}>
              Primary Color
            </Typography>
            <ColorPicker
              value={formData.invoice_primary_color}
              onChange={(color) => handleChange("invoice_primary_color", color)}
              openPicker={openPicker}
              onOpen={handleOpenPicker}
              pickerKey="invoice_primary"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" mb={1} fontSize={{ xs: 12, sm: 14 }}>
              Secondary Color
            </Typography>
            <ColorPicker
              value={formData.invoice_secondary_color}
              onChange={(color) => handleChange("invoice_secondary_color", color)}
              openPicker={openPicker}
              onOpen={handleOpenPicker}
              pickerKey="invoice_secondary"
            />
          </Grid>
        </Grid>

        {/* Footer */}
        <Typography
          variant="h6"
          fontSize={{ xs: 14, sm: 16 }}
          mt={isMobile ? 1.5 : 2}
          mb={isMobile? 2 : 1}
        >
          Invoice Footer
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={isMobile ? 3 : 2}
          value={formData.invoice_footer}
          onChange={(e) => handleChange("invoice_footer", e.target.value)}
          sx={{
            "& .MuiInputBase-root": { fontSize: { xs: 14, sm: 16 } },
            "& .MuiInputLabel-root": { fontSize: { xs: 12, sm: 14 } },
          }}
        />

        {/* Buttons */}
        <Grid
          container
          spacing={{ xs: 1, sm: 2 }}
          mt={isMobile ? 1.5 : 2}
          justifyContent={{ xs: "center", sm: "flex-end" }}
        >
          <Grid item xs={12} sm={3}>
            <Button
              fullWidth
              variant="contained"
              sx={{
                background: Color.secondary.main,
                color: "#fff",
                fontSize: { xs: 12, sm: 14 },
                height: { xs: 36, sm: 40 },
              }}
              onClick={() => {
                setFormData({
                  group: "invoice",
                  company_name: "",
                  gst_number: "",
                  company_phone_number: "",
                  billing_address: "",
                  company_logo: null,
                  website_primary_color: "",
                  website_secondary_color: "",
                  invoice_primary_color: "",
                  invoice_secondary_color: "",
                  invoice_footer: "",
                  invoice_template: "",
                });
                setLogoPreview("");
                setErrors({});
                localStorage.removeItem("settings");
              }}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                background: Color.primary.main,
                color: "#fff",
                fontSize: { xs: 12, sm: 14 },
                height: { xs: 36, sm: 40 },
              }}
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Settings;